import { API_BASE_URL } from "../config/api";

const BACKEND_BASE_URL = API_BASE_URL;

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function getTokenFromResponse(data) {
  // Try common direct token fields first.
  const direct =
    data?.token ||
    data?.idToken ||
    data?.accessToken ||
    data?.access_token ||
    data?.jwt ||
    data?.jwtToken ||
    data?.bearerToken;
  if (isNonEmptyString(direct)) return direct;

  // Then try common nesting patterns.
  const nested =
    data?.data?.token ||
    data?.data?.idToken ||
    data?.data?.accessToken ||
    data?.data?.access_token ||
    data?.result?.token ||
    data?.result?.idToken ||
    data?.result?.accessToken ||
    data?.result?.access_token ||
    data?.auth?.token ||
    data?.auth?.accessToken;
  if (isNonEmptyString(nested)) return nested;

  // Finally, do a bounded recursive search for common token keys.
  const tokenKeys = new Set([
    "token",
    "idToken",
    "accessToken",
    "access_token",
    "jwt",
    "jwtToken",
    "bearerToken",
  ]);

  const seen = new Set();
  const maxDepth = 8;

  function walk(value, depth) {
    if (depth > maxDepth) return null;
    if (!value || typeof value !== "object") return null;
    if (seen.has(value)) return null;
    seen.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = walk(item, depth + 1);
        if (found) return found;
      }
      return null;
    }

    for (const [k, v] of Object.entries(value)) {
      if (tokenKeys.has(k) && isNonEmptyString(v)) return v;
      const found = walk(v, depth + 1);
      if (found) return found;
    }
    return null;
  }

  return walk(data, 0);
}

function getExpiresInFromResponse(data) {
  return (
    data?.expiresIn ??
    data?.expires_in ??
    data?.expires ??
    data?.expiresSeconds
  );
}

export function getStoredToken() {
  try {
    const raw = localStorage.getItem("userDetails");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.idToken || parsed?.token || parsed?.accessToken || null;
  } catch {
    return null;
  }
}

export function authHeaders() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Calls your Node backend login endpoint and normalizes the response into
 * the shape expected by this app's Redux auth reducer.
 */
export async function login(email, password) {
  const res = await fetch(`${BACKEND_BASE_URL}/api/admin/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  const data = await res
    .json()
    .catch(() => ({}));

  if (!res.ok) {
    const message =
      data?.message ||
      data?.error ||
      data?.errorMessage ||
      `Login failed (${res.status})`;
    throw new Error(message);
  }

  const token = getTokenFromResponse(data);
  if (!token) {
    // Give a more actionable error to help you adjust token mapping.
    let sampleKeys = [];
    try {
      sampleKeys = data && typeof data === "object" ? Object.keys(data) : [];
    } catch {
      sampleKeys = [];
    }
    const details = sampleKeys.length ? ` Response top-level keys: ${sampleKeys.join(", ")}.` : "";
    throw new Error(
      `Login succeeded but backend did not return a token (expected one of token/idToken/accessToken/jwt).${details}`
    );
  }

  const expiresIn = getExpiresInFromResponse(data);

  // Backend response shape: { results: { data: { user: {...}, token: "..." } } }
  const userObj =
    data?.results?.data?.user ||
    data?.data?.data?.user    ||
    data?.data?.user          ||
    data?.user                ||
    {};

  return {
    // Match Redux selector: `state.auth.auth.idToken`
    idToken:      token,
    email:        userObj?.email        || data?.email        || email,
    userName:     userObj?.userName     || data?.userName      || "",
    mobileNumber: userObj?.mobileNumber || data?.mobileNumber || "",
    gender:       userObj?.gender       || data?.gender        || "",
    dob:          userObj?.dob          || data?.dob           || "",
    imageUrl:     userObj?.imageUrl     || data?.imageUrl      || "",
    roleId:       userObj?.roleId?._id  || userObj?.roleId  || data?.roleId  || "",
    roleName:     userObj?.roleId?.roleName || userObj?.roleName || "",
    userId:       userObj?._id          || data?.localId       || data?.userId || "",
    localId:      userObj?._id          || data?.localId       || data?.userId || "",
    isActive:     userObj?.isActive     ?? true,
    expiresIn:    typeof expiresIn === "number" ? expiresIn : undefined,
    refreshToken: data?.refreshToken    || data?.refresh_token || "",
  };
}

export async function authFetch(url, options = {}) {
  const headers = { ...(options.headers || {}), ...authHeaders() };
  return fetch(url, { ...options, headers });
}

export function getBackendBaseUrl() {
  return BACKEND_BASE_URL;
}

